import axios from "axios";
import { extractErrorMessage } from "./errorMessage";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Phase B (Code-Review): Session läuft über httpOnly-Cookies (Access 15 min, Refresh rotierend).
// Kein Token mehr in localStorage. CSRF per Double-Submit: Cookie `csrf_token` → Header `X-CSRF-Token`.
const UNSAFE = new Set(["post", "put", "patch", "delete"]);

export function getCsrfToken() {
  const m = document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/);
  return m ? decodeURIComponent(m[1]) : "";
}

export function csrfHeaders() {
  const t = getCsrfToken();
  return t ? { "X-CSRF-Token": t } : {};
}

// Für die wenigen rohen fetch()-Aufrufe (Blob-Downloads): Cookies + CSRF mitsenden.
export function authFetch(url, options = {}) {
  const method = (options.method || "GET").toLowerCase();
  const headers = { ...(options.headers || {}), ...(UNSAFE.has(method) ? csrfHeaders() : {}) };
  return fetch(url, { ...options, headers, credentials: "include" });
}

export const apiClient = axios.create({
  baseURL: API,
  withCredentials: true,
});

// Doppelklick-/Retry-Schutz: jede schreibende Anfrage bekommt einen Idempotenz-Schlüssel aus Methode+URL+Body.
// Identische Anfrage innerhalb von 15 s → Server liefert die gespeicherte Antwort statt erneut zu buchen.
async function autoIdempotencyKey(config) {
  const raw = `${config.method}|${config.url}|${typeof config.data === "string" ? config.data : JSON.stringify(config.data ?? null)}|${localStorage.getItem("authEmail") || ""}`;
  try {
    const buf = await crypto.subtle.digest("SHA-256", new TextEncoder().encode(raw));
    return "auto-" + Array.from(new Uint8Array(buf)).map((b) => b.toString(16).padStart(2, "0")).join("").slice(0, 40);
  } catch {
    let h = 0;
    for (let i = 0; i < raw.length; i++) h = (Math.imul(31, h) + raw.charCodeAt(i)) | 0;
    return "auto-" + (h >>> 0).toString(16) + "-" + raw.length;
  }
}

apiClient.interceptors.request.use(async (config) => {
  if (UNSAFE.has((config.method || "get").toLowerCase())) {
    const t = getCsrfToken();
    if (t) config.headers["X-CSRF-Token"] = t;
    const isForm = typeof FormData !== "undefined" && config.data instanceof FormData;
    if (!isForm && !config.headers["Idempotency-Key"] && !config.headers["idempotency-key"]) {
      config.headers["Idempotency-Key"] = await autoIdempotencyKey(config);
    }
  } else if (!config._slot) {
    // Lese-Aufrufe gestaffelt: max. MAX_PARALLEL_GETS gleichzeitig (Dashboard feuert sonst ~25 Aufrufe auf einmal → Server-Spitze/520).
    await acquireGetSlot(config);
    config._slot = true;
  }
  return config;
});

// ---- Gestaffelte GETs: kleine Prioritäts-Warteschlange (Kern-Aufrufe zuerst, Kacheln danach) ----
const MAX_PARALLEL_GETS = 4;
const CORE_GET = ["/auth/me", "/my/companies", "/dashboard-stats", "/onboarding/status", "/inbox", "/accounts"];
let _inflight = 0;
const _queue = [];

function acquireGetSlot(config) {
  if (_inflight < MAX_PARALLEL_GETS) { _inflight++; return Promise.resolve(); }
  const url = config.url || "";
  const prio = config.priority === "high" || CORE_GET.some((p) => url.includes(p)) ? 0 : 1;
  return new Promise((resolve) => {
    const entry = { prio, resolve };
    const idx = _queue.findIndex((e) => e.prio > prio);
    if (idx === -1) _queue.push(entry); else _queue.splice(idx, 0, entry);
  });
}

function releaseGetSlot() {
  const next = _queue.shift();
  if (next) next.resolve(); else _inflight = Math.max(0, _inflight - 1);
}

let _redirecting = false;
let _refreshPromise = null;

function clearClientSession() {
  try {
    localStorage.removeItem("authToken");
    localStorage.removeItem("authEmail");
    // Iter250: gewählte Firma behalten → nach erneutem Login landet man wieder im gleichen Mandanten
  } catch (e) {/* ignore */}
}

function redirectToLogin() {
  if (_redirecting) return;
  _redirecting = true;
  clearClientSession();
  if (typeof window !== "undefined" && !window.location.pathname.startsWith("/login")) {
    window.location.assign("/login?reason=session-expired");
  }
}

// Access-Cookie abgelaufen → einmal still per Refresh-Cookie erneuern, dann Request wiederholen.
function refreshSession() {
  if (!_refreshPromise) {
    _refreshPromise = axios
      .post(`${API}/auth/refresh`, null, { withCredentials: true, headers: csrfHeaders() })
      .finally(() => { _refreshPromise = null; });
  }
  return _refreshPromise;
}

const NO_REFRESH = ["/auth/refresh", "/auth/login", "/auth/login-password", "/auth/verify-code", "/auth/2fa-verify-login", "/auth/register", "/auth/logout"];

function releaseFor(cfg) {
  if (cfg && cfg._slot && !cfg._released) { cfg._released = true; releaseGetSlot(); }
}

let _replayToastAt = 0;
apiClient.interceptors.response.use(
  (response) => {
    releaseFor(response?.config);
    if (response?.headers?.["idempotent-replayed"] === "true" && Date.now() - _replayToastAt > 3000) {
      _replayToastAt = Date.now();
      toast.info("Doppelte Anfrage erkannt – bereits ausgeführt, nicht erneut gebucht.");
    }
    return response;
  },
  async (error) => {
    releaseFor(error?.config);
    const status = error?.response?.status;
    const url = (error?.config?.url || "");
    const cfg = error?.config || {};
    const skip = NO_REFRESH.some((p) => url.includes(p));
    const loggedIn = typeof window !== "undefined" && !!localStorage.getItem("authEmail");

    if (status === 401 && !skip && !cfg._retried && loggedIn && !_redirecting) {
      try {
        await refreshSession();
        cfg._retried = true;
        cfg._slot = false; cfg._released = false;
        return apiClient.request(cfg);
      } catch (e) {
        redirectToLogin();
      }
    } else if (status === 401 && !skip && cfg._retried && loggedIn) {
      // Refresh hat nicht gereicht → Session ist tot
      const isIdentityCall = url.includes("/auth/me") || url.endsWith("/my/companies");
      if (isIdentityCall) redirectToLogin();
    }

    // 2FA-Pflicht der Firma: einmalig Hinweis + zu «Mein Account» leiten
    if (status === 403 && error?.response?.data?.detail?.code === "2fa_required" && typeof window !== "undefined") {
      if (!window.__tfaRedirected) {
        window.__tfaRedirected = true;
        try { sessionStorage.setItem("tfaRequiredMessage", error.response.data.detail.message || ""); } catch (e) {/* ignore */}
        if (!window.location.pathname.startsWith("/account")) window.location.assign("/account?reason=2fa-required");
      }
    }

    // Iter154: FastAPI 422-Validation-Errors haben `detail` als Array von Objekten.
    // Wir konvertieren `detail` global in einen lesbaren String, damit alle
    // `e.response?.data?.detail || "..."` Patterns im Code sicher sind.
    if (error?.response?.data?.detail && typeof error.response.data.detail !== "string") {
      const str = extractErrorMessage(error, "");
      if (str) {
        error.response.data._rawDetail = error.response.data.detail;
        error.response.data.detail = str;
      }
    }
    return Promise.reject(error);
  },
);

// Session-Ende: Server widerruft Refresh-Token + löscht Cookies; Client vergisst E-Mail.
export async function logoutRequest() {
  try { await apiClient.post("/auth/logout"); } catch (e) {/* Cookies evtl. schon weg */}
  clearClientSession();
}
